import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Mail, Send, Inbox, CheckCircle2, AlertTriangle, Sparkles, Unplug, RefreshCw, PenLine } from 'lucide-react';
import SignatureEditor from './SignatureEditor';

// Unified "Email & Inbox" hub summary: outbound + inbound health at a glance,
// with the primary "Connect Mail" action that opens the guided wizard.
// Reuses existing parent state (emailIdentity, smtp integration, handlers) —
// no new backend calls.
export default function MailHub({ emailIdentity, smtpIntegration, gmailConnected, onConnectMail, onSendTest, onDisconnectMail, onReconnectMail, reconnecting, onSwitchDc }) {
  const [signatureOpen, setSignatureOpen] = React.useState(false);
  const outboundConnected = !!emailIdentity?.connected;
  const providerLabel = emailIdentity?.provider === 'sendgrid' ? 'SendGrid'
    : emailIdentity?.provider === 'gmail' ? 'Gmail' : emailIdentity?.provider === 'smtp' ? 'SMTP' : null;

  const imapStatus = smtpIntegration?.imap_status;
  const inboundConnected = imapStatus === 'connected' || !!gmailConnected;
  const inboundNeedsAttention = imapStatus === 'needs_attention';
  // Item 4: detect the current Zoho data center so we can offer a one-tap swap.
  const imapHost = (smtpIntegration?.imap_host || '').toLowerCase();
  const isZoho = imapHost.includes('zoho');
  const onCom = imapHost.endsWith('.com');
  const onIn = imapHost.endsWith('.in');
  const swapTarget = onCom ? 'in' : onIn ? 'com' : null;
  const showSwitchDc = !!onSwitchDc && inboundNeedsAttention && isZoho && !!swapTarget;
  const lastErr = (smtpIntegration?.imap_last_error || '').replace(/^auth:\s*/i, '').trim();

  const Pill = ({ ok, warn, children, testid }) => (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border ${
        ok ? 'text-emerald-600 border-emerald-500/30 bg-emerald-500/5'
          : warn ? 'text-amber-600 border-amber-500/30 bg-amber-500/5'
          : 'text-muted-foreground border-border bg-muted/40'}`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : warn ? <AlertTriangle className="h-3.5 w-3.5" /> : <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />}
      {children}
    </span>
  );

  return (
    <div className="mb-6 rounded-xl border border-border bg-card p-4" data-testid="mail-hub">
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Mail className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold">Email &amp; Inbox</p>
            <Pill ok={outboundConnected} testid="mail-hub-outbound-pill">
              <Send className="h-3 w-3" /> Outbound {outboundConnected ? '✓' : 'not set'}
            </Pill>
            <Pill ok={inboundConnected} warn={inboundNeedsAttention} testid="mail-hub-inbound-pill">
              <Inbox className="h-3 w-3" /> Inbound {inboundConnected ? '✓' : inboundNeedsAttention ? 'check' : 'not set'}
            </Pill>
          </div>
          <p className="text-xs text-muted-foreground mt-1" data-testid="email-sending-identity">
            {outboundConnected
              ? <>Customer emails send as <span className="font-medium text-foreground">{emailIdentity.from_name ? `${emailIdentity.from_name} ` : ''}&lt;{emailIdentity.from_email}&gt;</span> via {providerLabel}.</>
              : <>No sending identity connected — customer emails (leads, quotes, invoices) won't be sent until you connect your mail.</>}
          </p>
          {inboundNeedsAttention && (
            <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2" data-testid="mail-hub-inbound-error">
              <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>Inbound reply capture needs attention{lastErr ? ` — ${lastErr}` : '.'}</span>
              </p>
              {showSwitchDc && (
                <div className="mt-2">
                  <p className="text-[11px] text-amber-700/90 dark:text-amber-400/90 mb-1.5">
                    Looks like a Zoho <span className="font-medium">{onCom ? 'India' : 'Global'}</span> account on the wrong data center.
                  </p>
                  <Button size="sm" variant="outline" onClick={() => onSwitchDc(swapTarget)} disabled={reconnecting}
                    data-testid="mail-hub-switch-dc-btn"
                    className="border-amber-500/40 text-amber-700 hover:text-amber-800 dark:text-amber-400">
                    <RefreshCw className={`h-3.5 w-3.5 mr-1 ${reconnecting ? 'animate-spin' : ''}`} />
                    {swapTarget === 'in' ? 'Switch to India (.in) servers' : 'Switch to Global (.com) servers'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button size="sm" onClick={onConnectMail} data-testid="connect-mail-btn"
            className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Sparkles className="h-4 w-4 mr-1" /> {outboundConnected ? 'Manage mail' : 'Connect Mail'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSignatureOpen(true)} data-testid="edit-signature-btn">
            <PenLine className="h-4 w-4 mr-1" /> Signature
          </Button>
          {outboundConnected && (
            <Button size="sm" variant="outline" onClick={onSendTest} data-testid="send-test-email-banner-btn">
              <Mail className="h-4 w-4 mr-1" /> Send test
            </Button>
          )}
          {outboundConnected && onReconnectMail && (
            <Button size="sm" variant="outline" onClick={onReconnectMail} disabled={reconnecting}
              data-testid="reconnect-mail-btn"
              className={inboundNeedsAttention ? 'border-amber-500/40 text-amber-600 hover:text-amber-700' : ''}>
              <RefreshCw className={`h-4 w-4 mr-1 ${reconnecting ? 'animate-spin' : ''}`} />
              {reconnecting ? 'Reconnecting…' : 'Reconnect'}
            </Button>
          )}
          {outboundConnected && onDisconnectMail && (
            <Button size="sm" variant="ghost" onClick={onDisconnectMail} data-testid="disconnect-mail-btn"
              className="text-muted-foreground hover:text-red-500">
              <Unplug className="h-4 w-4 mr-1" /> Disconnect
            </Button>
          )}
        </div>
      </div>
      <SignatureEditor open={signatureOpen} onOpenChange={setSignatureOpen} />
    </div>
  );
}
